// Converts wire DTOs (PascalCase, 1:1 with the backend) into this feature's camelCase domain
// models. `toMessage`/`toMessages` take the current user's id explicitly (instead of reading it
// from a store/AuthStore here) so this mapper stays in `data/`, which must not import
// `presentation/` (see `tools/check-architecture.mjs`'s "data layer imports presentation" rule) --
// same "resolve identity in the caller, pass it in" pattern `ClientDetailStore.loadConsents`
// already uses for `organizationId`.

import { Injectable } from '@angular/core';
import { ChatClientPolicyDto } from '../dtos/chat-client-policy.dto';
import { ChatConversationDto } from '../dtos/chat-conversation.dto';
import { ChatMessageDto } from '../dtos/chat-message.dto';
import { ChatParticipantDto } from '../dtos/chat-participant.dto';
import { ChatClientPolicy } from '../../domain/models/chat-client-policy.model';
import { Conversation } from '../../domain/models/conversation.model';
import { Message, MessageAttachment, MessageKind } from '../../domain/models/message.model';
import { Participant } from '../../domain/models/participant.model';

@Injectable()
export class ChatMapper {
  public toParticipant(dto: ChatParticipantDto): Participant {
    return {
      userId: dto.UserId,
      username: dto.Username,
      fullName: dto.FullName ?? null,
      joinedAtUtc: dto.JoinedAtUtc,
      lastReadMessageId: dto.LastReadMessageId !== null ? String(dto.LastReadMessageId) : null,
      lastReadAtUtc: dto.LastReadAtUtc,
    };
  }

  public toConversation(dto: ChatConversationDto): Conversation {
    return {
      id: dto.ConversationId,
      isGroup: dto.IsGroup,
      title: dto.Title,
      lastMessagePreview: dto.LastMessagePreview,
      lastMessageSentAtUtc: dto.LastMessageSentAtUtc,
      unreadCount: dto.UnreadCount,
      // `?? 0` defensively covers an older cached response predating this field, matching the
      // fail-safe default `ChatConversationDto.UnseenClinicalRequestCount`'s own doc-comment
      // promises server-side.
      unseenClinicalRequestCount: dto.UnseenClinicalRequestCount ?? 0,
      pendingClinicalRequestCount: dto.PendingClinicalRequestCount ?? 0,
      participants: dto.Participants.map(participant => this.toParticipant(participant)),
      clientUserId: dto.ClientUserId,
      clientDisplayName: dto.ClientDisplayName,
      clientAvatarUrl: dto.ClientAvatarUrl,
      departmentId: dto.DepartmentId,
      departmentName: dto.DepartmentName,
    };
  }

  public toMessage(dto: ChatMessageDto, currentUserId: string | null): Message {
    const attachment = this.toMessageAttachment(dto);

    return {
      id: String(dto.MessageId),
      conversationId: dto.ConversationId,
      senderUserId: dto.SenderUserId,
      senderUsername: dto.SenderUsername,
      senderFullName: dto.SenderFullName ?? null,
      senderAvatarUrl: dto.SenderAvatarUrl ?? null,
      body: dto.Body,
      sentAtUtc: dto.SentAtUtc,
      clientMessageId: dto.ClientMessageId,
      isMine: currentUserId !== null && dto.SenderUserId === currentUserId,
      kind: this.toMessageKind(dto.Kind, attachment !== null),
      ...(attachment !== null ? { attachment } : {}),
      ...(dto.SubjectId ? { subjectId: dto.SubjectId } : {}),
    };
  }

  public toMessages(dtos: readonly ChatMessageDto[], currentUserId: string | null): readonly Message[] {
    return dtos.map(dto => this.toMessage(dto, currentUserId));
  }

  public toChatClientPolicy(dto: ChatClientPolicyDto): ChatClientPolicy {
    return {
      audioEnabled: dto.AudioEnabled,
      filesEnabled: dto.FilesEnabled,
    };
  }

  // `Kind` is optional on the wire DTO (see its doc-comment), so an absent value falls back to the
  // same thing the backend's own column default does -- with one refinement: a payload that carries
  // an attachment but no `Kind` (a fixture, or a response from a build predating the column) is
  // still reported as `'attachment'`, so renderers can branch on `kind` alone.
  private toMessageKind(value: string | null | undefined, hasAttachment: boolean): MessageKind {
    if (
      value === 'text' || value === 'attachment' || value === 'appointment_request' || value === 'clinical_request'
      || value === 'pet_deceased_notice'
    ) {
      return value;
    }
    return hasAttachment ? 'attachment' : 'text';
  }

  // `null` (not just "all fields absent") whenever `AttachmentDocumentPublicId` is missing -- the
  // backend only ever sets the other `Attachment*` fields alongside it (see `ChatMessageDto.cs`),
  // so this single field is enough to decide "is this an attachment message at all".
  private toMessageAttachment(dto: ChatMessageDto): MessageAttachment | null {
    if (dto.AttachmentDocumentPublicId === null || dto.AttachmentDocumentPublicId === undefined) return null;

    return {
      documentPublicId: dto.AttachmentDocumentPublicId,
      fileName: dto.AttachmentFileName ?? '',
      contentType: dto.AttachmentContentType ?? '',
      sizeBytes: dto.AttachmentSizeBytes ?? 0,
      ...(dto.AttachmentViewedAtUtc !== null && dto.AttachmentViewedAtUtc !== undefined
        ? { viewedAtUtc: dto.AttachmentViewedAtUtc } : {}),
      ...(dto.AttachmentDurationSeconds !== null && dto.AttachmentDurationSeconds !== undefined
        ? { durationSeconds: dto.AttachmentDurationSeconds } : {}),
    };
  }
}
